import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Camera, Square, Download, RotateCcw, Music, Upload,
  Loader2, X, Mic, MicOff, ArrowLeft, Volume2, VolumeX, Share2,
  Scissors, Link, ChevronDown, ChevronUp, Check, RefreshCw, Film
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_DURATION = 60;
const FRAME_URL = `${BACKEND_URL}/api/static/frames/cca_frame_portrait.png`;
const REC_W = 720;
const REC_H = 960;

/* ─── Shared download / share helpers ─── */
const downloadFromUrl = (url, name) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const shareVideoUrl = async (videoUrl) => {
  const text = 'You are invited to the CCA Cultural Festival 2026! Hosted by NGOKETUNJIA (NGODECA) - July 18, 2026 at Park Lacharit\u00e9, LaSalle, QC';
  const fileName = 'CCA_Festival_2026_Invitation.mp4';

  // Try fetching as blob for native file sharing (mobile share sheet)
  try {
    if (navigator.share && navigator.canShare) {
      const resp = await fetch(videoUrl);
      const blob = await resp.blob();
      const file = new File([blob], fileName, { type: 'video/mp4' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'CCA Cultural Festival 2026', text });
        return;
      }
    }
  } catch (e) {
    if (e.name === 'AbortError') return;
    // Fall through to download + text share
  }

  // Fallback: download + share text
  downloadFromUrl(videoUrl, fileName);
  try {
    if (navigator.share) await navigator.share({ title: 'CCA Cultural Festival 2026', text });
  } catch { /* cancelled */ }
};

/* ─── Result buttons (shared by Record and Upload) ─── */
const ResultActions = ({ processing, videoUrl, processingError, onRetry, onReset, resetLabel }) => (
  <div className="space-y-3">
    {processing && (
      <div className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-cameroon-yellow/10 border border-cameroon-yellow/20" data-testid="processing-status">
        <Loader2 className="w-4 h-4 text-cameroon-yellow animate-spin flex-shrink-0" />
        <span className="text-xs text-cameroon-yellow">Adding CCA frame & intro to your video...</span>
      </div>
    )}
    {videoUrl && !processing && (
      <div className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20" data-testid="processing-done">
        <Check className="w-4 h-4 text-cameroon-green flex-shrink-0" />
        <span className="text-xs text-cameroon-green">Video ready with CCA frame & intro!</span>
      </div>
    )}
    {processingError && !processing && (
      <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="processing-error">
        <span className="text-xs text-red-400">Processing failed. Please retry.</span>
        <Button size="sm" variant="ghost" onClick={onRetry} className="text-cameroon-yellow hover:bg-cameroon-yellow/10 h-7 px-2 text-xs" data-testid="retry-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Retry
        </Button>
      </div>
    )}
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={onReset} className="border-white/20 text-white hover:bg-white/10 h-10 flex-1" data-testid="retake-btn">
        <RotateCcw className="w-4 h-4 mr-1" /> {resetLabel || 'Start Over'}
      </Button>
      <Button onClick={() => videoUrl && downloadFromUrl(videoUrl, 'CCA_Festival_2026_Invitation.mp4')} disabled={!videoUrl || processing} className="bg-cameroon-green hover:bg-cameroon-green/90 h-10 flex-1 disabled:opacity-40" data-testid="download-video-btn">
        {processing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
        {processing ? 'Processing...' : 'Download MP4'}
      </Button>
    </div>
    <Button onClick={() => videoUrl && shareVideoUrl(videoUrl)} disabled={!videoUrl || processing} className="bg-blue-600 hover:bg-blue-700 h-10 w-full touch-manipulation disabled:opacity-40" data-testid="share-video-btn">
      {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
      {processing ? 'Processing...' : 'Share to WhatsApp & More'}
    </Button>
  </div>
);

const VideoRecorderPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('record'); // 'record' | 'upload'

  /* ─── RECORD mode state ─── */
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const animFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const musicNodeRef = useRef(null);
  const musicGainRef = useRef(null);
  const recordingFormat = useRef(null);
  const streamRef = useRef(null);
  const recProcessingId = useRef(0);
  const facingModeRef = useRef('user');
  const musicAudioRef = useRef(null);

  const [phase, setPhase] = useState('setup');
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [facingMode, setFacingMode] = useState('user');
  const [recProcessing, setRecProcessing] = useState(false);
  const [recVideoUrl, setRecVideoUrl] = useState(null);
  const [recError, setRecError] = useState(false);

  const [showMusic, setShowMusic] = useState(false);
  const [musicAudioUrl, setMusicAudioUrl] = useState(null);
  const [musicTitle, setMusicTitle] = useState('');
  const [musicSource, setMusicSource] = useState('none');
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicVolume, setMusicVolume] = useState(25);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioUrlInput, setAudioUrlInput] = useState('');
  const [frameImg, setFrameImg] = useState(null);

  /* ─── UPLOAD mode state ─── */
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [uplProcessing, setUplProcessing] = useState(false);
  const [uplVideoUrl, setUplVideoUrl] = useState(null);
  const [uplError, setUplError] = useState(false);
  const uplProcessingId = useRef(0);

  useEffect(() => { facingModeRef.current = facingMode; }, [facingMode]);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setFrameImg(img);
    img.src = FRAME_URL;
  }, []);

  /* ─── Camera setup ─── */
  const startCamera = useCallback(async () => {
    if (mode !== 'record') return;
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 720 }, height: { ideal: 960 } },
        audio: micEnabled ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true } : false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setCameraReady(true);
    } catch { /* camera access denied */ }
  }, [facingMode, micEnabled, mode]);

  useEffect(() => {
    if (mode === 'record') startCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close();
    };
  }, [startCamera, mode]);

  useEffect(() => {
    if (!cameraReady || phase === 'preview' || mode !== 'record') return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    const maxW = Math.min(window.innerWidth - 32, 400);
    canvas.width = maxW;
    canvas.height = Math.round(maxW * (REC_H / REC_W));
    const dW = canvas.width, dH = canvas.height;
    const draw = () => {
      if (video.readyState >= 2) {
        const vw = video.videoWidth, vh = video.videoHeight;
        const srcR = vw / vh, dstR = dW / dH;
        let sx = 0, sy = 0, sw = vw, sh = vh;
        if (srcR > dstR) { sw = vh * dstR; sx = (vw - sw) / 2; } else { sh = vw / dstR; sy = (vh - sh) / 2; }
        ctx.save();
        if (facingMode === 'user') { ctx.scale(-1, 1); ctx.drawImage(video, sx, sy, sw, sh, -dW, 0, dW, dH); }
        else { ctx.drawImage(video, sx, sy, sw, sh, 0, 0, dW, dH); }
        ctx.restore();
        if (frameImg) ctx.drawImage(frameImg, 0, 0, dW, dH);
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [cameraReady, phase, facingMode, frameImg, mode]);

  /* ─── Music handlers ─── */
  const handleMusicUpload = (e) => { const file = e.target.files[0]; if (!file) return; setMusicTitle(file.name.replace(/\.[^/.]+$/, '')); setMusicAudioUrl(URL.createObjectURL(file)); setMusicSource('upload'); setTrimStart(0); setTrimEnd(0); };
  const handleAudioUrl = () => { if (!audioUrlInput.trim()) return; setMusicAudioUrl(audioUrlInput.trim()); setMusicTitle('Audio from URL'); setMusicSource('url'); setTrimStart(0); setTrimEnd(0); };
  const clearMusic = () => { if (musicAudioRef.current) { musicAudioRef.current.pause(); musicAudioRef.current.currentTime = 0; } if (musicAudioUrl && musicSource === 'upload') URL.revokeObjectURL(musicAudioUrl); setMusicSource('none'); setAudioUrlInput(''); setMusicAudioUrl(null); setMusicTitle(''); setMusicPlaying(false); setTrimStart(0); setTrimEnd(0); setAudioDuration(0); };
  const onAudioLoaded = () => { if (musicAudioRef.current) { const dur = Math.floor(musicAudioRef.current.duration); setAudioDuration(dur); if (trimEnd === 0) setTrimEnd(Math.min(dur, 60)); } };
  const toggleMusicPreview = () => { if (!musicAudioRef.current) return; if (musicPlaying) { musicAudioRef.current.pause(); setMusicPlaying(false); } else { musicAudioRef.current.currentTime = trimStart; musicAudioRef.current.volume = musicVolume / 100; musicAudioRef.current.play(); setMusicPlaying(true); } };
  useEffect(() => { if (!musicPlaying || !musicAudioRef.current) return; const iv = setInterval(() => { if (musicAudioRef.current && musicAudioRef.current.currentTime >= trimEnd) { musicAudioRef.current.pause(); setMusicPlaying(false); } }, 200); return () => clearInterval(iv); }, [musicPlaying, trimEnd]);

  /* ─── Recording ─── */
  const getRecordingMime = useCallback(() => {
    for (const t of ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4;codecs=avc1,mp4a', 'video/mp4']) { if (MediaRecorder.isTypeSupported(t)) return { mime: t, ext: 'mp4', type: 'video/mp4' }; }
    for (const t of ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm']) { if (MediaRecorder.isTypeSupported(t)) return { mime: t, ext: 'webm', type: 'video/webm' }; }
    return { mime: 'video/webm', ext: 'webm', type: 'video/webm' };
  }, []);

  const startRecording = () => { setCountdown(3); setPhase('countdown'); if (musicAudioRef.current) { musicAudioRef.current.pause(); musicAudioRef.current.currentTime = trimStart; } let c = 3; const iv = setInterval(() => { c--; setCountdown(c); if (c <= 0) { clearInterval(iv); doRecord(); } }, 1000); };

  const doRecord = () => {
    const videoTracks = streamRef.current.getVideoTracks();
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioCtx;
    const dest = audioCtx.createMediaStreamDestination();
    if (streamRef.current) { const tracks = streamRef.current.getAudioTracks(); if (tracks.length > 0) { const mic = audioCtx.createMediaStreamSource(new MediaStream(tracks)); const hp = audioCtx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 85; const comp = audioCtx.createDynamicsCompressor(); comp.threshold.value = -24; comp.knee.value = 30; comp.ratio.value = 4; mic.connect(hp); hp.connect(comp); comp.connect(dest); } }
    if (musicAudioUrl && musicAudioRef.current) { const el = musicAudioRef.current; el.currentTime = trimStart; el.loop = false; try { if (!musicNodeRef.current) musicNodeRef.current = audioCtx.createMediaElementSource(el); const gain = audioCtx.createGain(); gain.gain.value = musicVolume / 100; musicGainRef.current = gain; musicNodeRef.current.connect(gain); gain.connect(dest); el.play(); setMusicPlaying(true); } catch { el.play(); setMusicPlaying(true); } }
    const combined = new MediaStream([...videoTracks, ...dest.stream.getAudioTracks()]);
    chunksRef.current = [];
    const fmt = getRecordingMime(); recordingFormat.current = fmt;
    let recorder; try { recorder = new MediaRecorder(combined, { mimeType: fmt.mime, videoBitsPerSecond: 2500000 }); } catch { alert('Recording is not supported on this browser.'); setPhase('setup'); return; }
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onerror = () => { try { clearInterval(recorder._timer); } catch { /* ok */ } if (chunksRef.current.length > 0) { const blob = new Blob(chunksRef.current, { type: fmt.type }); setRecordedBlob(blob); setPreviewUrl(URL.createObjectURL(blob)); setPhase('preview'); } else { setPhase('setup'); } if (musicAudioRef.current) { musicAudioRef.current.pause(); setMusicPlaying(false); } };
    recorder.onstop = () => { const blob = new Blob(chunksRef.current, { type: fmt.type }); setRecordedBlob(blob); setPreviewUrl(URL.createObjectURL(blob)); setPhase('preview'); if (musicAudioRef.current) { musicAudioRef.current.pause(); setMusicPlaying(false); } if (audioContextRef.current && audioContextRef.current.state !== 'closed') { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; musicNodeRef.current = null; } };
    try { recorder.start(1000); } catch { alert('Failed to start recording.'); setPhase('setup'); return; }
    setPhase('recording'); setElapsed(0);
    const t0 = Date.now();
    const timer = setInterval(() => { const s = Math.floor((Date.now() - t0) / 1000); setElapsed(s); if (musicAudioRef.current && trimEnd > 0 && musicAudioRef.current.currentTime >= trimEnd) musicAudioRef.current.pause(); if (s >= MAX_DURATION) { clearInterval(timer); try { recorder.stop(); } catch { /* ok */ } } }, 200);
    recorder._timer = timer;
  };

  const stopRecording = () => { const rec = mediaRecorderRef.current; if (!rec) return; try { clearInterval(rec._timer); } catch { /* ok */ } try { if (rec.state !== 'inactive') rec.stop(); } catch { if (chunksRef.current.length > 0) { const blob = new Blob(chunksRef.current, { type: recordingFormat.current?.type || 'video/webm' }); setRecordedBlob(blob); setPreviewUrl(URL.createObjectURL(blob)); setPhase('preview'); } else { setPhase('setup'); } } if (musicAudioRef.current) { musicAudioRef.current.pause(); setMusicPlaying(false); } };

  const resetRecording = () => { recProcessingId.current++; if (previewUrl) URL.revokeObjectURL(previewUrl); setRecordedBlob(null); setPreviewUrl(null); setPhase('setup'); setElapsed(0); setRecProcessing(false); setRecVideoUrl(null); setRecError(false); startCamera(); };

  /* ─── Auto-process recorded video (with auto-retry) ─── */
  const processRecording = useCallback(async (blob, id, attempt = 1) => {
    setRecProcessing(true); setRecVideoUrl(null); setRecError(false);
    try {
      const fd = new FormData();
      fd.append('main', blob, `main.${recordingFormat.current?.ext || 'webm'}`);
      fd.append('mirror', facingModeRef.current === 'user' ? 'true' : 'false');
      const res = await axios.post(`${API_URL}/video/build-final`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      if (recProcessingId.current !== id) return;
      // Use download_url (with Content-Disposition headers) for mobile compatibility
      setRecVideoUrl(`${BACKEND_URL}${res.data.download_url || res.data.video_url}`);
    } catch (err) {
      if (recProcessingId.current !== id) return;
      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 2000));
        if (recProcessingId.current === id) return processRecording(blob, id, attempt + 1);
      }
      console.error('Processing failed after retries:', err?.message);
      setRecError(true);
    } finally { if (recProcessingId.current === id) setRecProcessing(false); }
  }, []);

  useEffect(() => { if (!recordedBlob) return; const id = ++recProcessingId.current; processRecording(recordedBlob, id); }, [recordedBlob, processRecording]);

  const retryRecording = () => { if (!recordedBlob) return; const id = ++recProcessingId.current; processRecording(recordedBlob, id); };

  /* ─── Upload mode handlers ─── */
  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    setUploadFile(file);
    setUploadPreviewUrl(URL.createObjectURL(file));
    setUplVideoUrl(null); setUplError(false);
  };

  const processUpload = useCallback(async (file, id, attempt = 1) => {
    setUplProcessing(true); setUplVideoUrl(null); setUplError(false);
    try {
      const fd = new FormData();
      fd.append('video', file, file.name);
      const res = await axios.post(`${API_URL}/video/upload-and-frame`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      if (uplProcessingId.current !== id) return;
      setUplVideoUrl(`${BACKEND_URL}${res.data.download_url || res.data.video_url}`);
    } catch (err) {
      if (uplProcessingId.current !== id) return;
      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 2000));
        if (uplProcessingId.current === id) return processUpload(file, id, attempt + 1);
      }
      console.error('Upload processing failed after retries:', err?.message);
      setUplError(true);
    } finally { if (uplProcessingId.current === id) setUplProcessing(false); }
  }, []);

  const startUploadProcessing = () => { if (!uploadFile) return; const id = ++uplProcessingId.current; processUpload(uploadFile, id); };
  const retryUpload = () => { if (!uploadFile) return; const id = ++uplProcessingId.current; processUpload(uploadFile, id); };
  const resetUpload = () => { uplProcessingId.current++; if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl); setUploadFile(null); setUploadPreviewUrl(null); setUplProcessing(false); setUplVideoUrl(null); setUplError(false); };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white/60 hover:text-white" data-testid="back-home">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="font-serif text-sm sm:text-base font-bold text-white" data-testid="recorder-title">Festival Invitation Video</h1>
        <div className="w-16" />
      </div>

      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Mode toggle */}
        <div className="flex rounded-lg overflow-hidden border border-white/10" data-testid="mode-toggle">
          <button onClick={() => setMode('record')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-colors ${mode === 'record' ? 'bg-cameroon-red text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-testid="mode-record">
            <Camera className="w-3.5 h-3.5" /> Record
          </button>
          <button onClick={() => setMode('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-colors ${mode === 'upload' ? 'bg-cameroon-green text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-testid="mode-upload">
            <Upload className="w-3.5 h-3.5" /> Upload Video
          </button>
        </div>

        {/* ══════════ RECORD MODE ══════════ */}
        {mode === 'record' && (
          <>
            <div className="relative bg-black rounded-xl overflow-hidden mx-auto" style={{ width: '100%', aspectRatio: `${REC_W}/${REC_H}` }} data-testid="video-viewport">
              <video ref={videoRef} className="hidden" muted playsInline />
              {phase !== 'preview' && <canvas ref={canvasRef} className="w-full h-full block" data-testid="recording-canvas" />}
              {phase === 'preview' && previewUrl && <video src={previewUrl} controls autoPlay loop playsInline className="w-full h-full object-contain bg-black" data-testid="preview-video" />}
              {phase === 'countdown' && <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10"><span className="text-7xl font-bold text-cameroon-yellow drop-shadow-lg animate-pulse">{countdown}</span></div>}
              {phase === 'recording' && <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600/90 z-10 shadow-lg"><div className="w-2 h-2 rounded-full bg-white animate-pulse" /><span className="text-white text-xs font-mono font-bold">{fmtTime(elapsed)} / {fmtTime(MAX_DURATION)}</span></div>}
              {!cameraReady && phase !== 'preview' && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900"><Loader2 className="w-7 h-7 text-cameroon-yellow animate-spin" /><p className="text-white/50 text-xs">Starting camera...</p></div>}
            </div>

            <div className="space-y-3">
              {phase === 'setup' && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setMicEnabled(m => !m)} className="border-white/20 text-white hover:bg-white/10 h-9 w-9 p-0" data-testid="mic-toggle">{micEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4 text-red-400" />}</Button>
                  <Button variant="outline" size="sm" onClick={() => setFacingMode(f => f === 'user' ? 'environment' : 'user')} className="border-white/20 text-white hover:bg-white/10 h-9 w-9 p-0" data-testid="flip-camera"><RotateCcw className="w-4 h-4" /></Button>
                  <Button onClick={startRecording} disabled={!cameraReady} className="bg-cameroon-red hover:bg-cameroon-red/90 h-10 px-5 text-sm" data-testid="start-recording-btn"><Camera className="w-4 h-4 mr-2" /> Start Recording</Button>
                </div>
              )}
              {phase === 'recording' && <div className="flex justify-center"><Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700 h-12 px-6 text-base touch-manipulation" data-testid="stop-recording-btn"><Square className="w-5 h-5 mr-2" /> Stop Recording ({MAX_DURATION - elapsed}s)</Button></div>}
              {phase === 'preview' && <ResultActions processing={recProcessing} videoUrl={recVideoUrl} processingError={recError} onRetry={retryRecording} onReset={resetRecording} resetLabel="Re-take" />}
            </div>

            {/* Music panel */}
            {phase === 'setup' && (
              <div className="border border-white/10 rounded-lg overflow-hidden">
                <button onClick={() => setShowMusic(!showMusic)} className="w-full flex items-center justify-between p-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors" data-testid="music-toggle">
                  <div className="flex items-center gap-2"><Music className="w-4 h-4 text-cameroon-yellow" /><span className="text-xs font-medium">{musicAudioUrl ? `\u266A ${musicTitle}` : 'Add Background Music'}</span></div>
                  {showMusic ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showMusic && (
                  <div className="p-3 pt-0 space-y-2.5 border-t border-white/5">
                    {musicAudioUrl ? (
                      <div className="space-y-2.5">
                        <div className="p-2 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20"><p className="text-[9px] text-white/50">{musicSource === 'url' ? 'From URL' : 'Uploaded'}</p><p className="text-[11px] text-white font-medium truncate">{musicTitle}</p></div>
                        <div className="space-y-1"><Label className="text-white/60 text-[10px] flex items-center gap-1"><Volume2 className="w-3 h-3" /> Volume: {musicVolume}%</Label><input type="range" min="5" max="50" value={musicVolume} onChange={(e) => { const v = parseInt(e.target.value); setMusicVolume(v); if (musicAudioRef.current) musicAudioRef.current.volume = v / 100; if (musicGainRef.current) musicGainRef.current.gain.value = v / 100; }} className="w-full h-1.5 accent-cameroon-yellow" data-testid="volume-slider" /></div>
                        {audioDuration > 0 && <div className="space-y-1"><Label className="text-white/60 text-[10px] flex items-center gap-1"><Scissors className="w-3 h-3" /> Trim: {fmtSec(trimStart)} — {fmtSec(trimEnd)}</Label><div className="flex gap-2 items-center"><div className="flex-1 space-y-0.5"><span className="text-[8px] text-white/40">Start</span><input type="range" min="0" max={Math.max(audioDuration - 5, 0)} value={trimStart} onChange={(e) => { const v = parseInt(e.target.value); setTrimStart(v); if (v >= trimEnd) setTrimEnd(Math.min(v + 10, audioDuration)); }} className="w-full h-1 accent-cameroon-green" data-testid="trim-start" /></div><div className="flex-1 space-y-0.5"><span className="text-[8px] text-white/40">End</span><input type="range" min={trimStart + 5} max={audioDuration} value={trimEnd} onChange={(e) => setTrimEnd(parseInt(e.target.value))} className="w-full h-1 accent-cameroon-red" data-testid="trim-end" /></div></div></div>}
                        <div className="flex gap-1.5"><Button size="sm" variant="outline" onClick={toggleMusicPreview} className="flex-1 h-7 border-white/20 text-white hover:bg-white/10 text-[10px]" data-testid="preview-music-btn">{musicPlaying ? <><VolumeX className="w-3 h-3 mr-1" />Pause</> : <><Volume2 className="w-3 h-3 mr-1" />Preview</>}</Button><Button size="sm" variant="ghost" onClick={clearMusic} className="h-7 text-red-400 hover:bg-red-400/10 px-2"><X className="w-3 h-3" /></Button></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-white/20 hover:border-white/40 cursor-pointer" data-testid="music-upload-label"><Upload className="w-3.5 h-3.5 text-white/50" /><span className="text-[10px] text-white/50 truncate">Upload MP3 / WAV...</span><input type="file" accept="audio/*" onChange={handleMusicUpload} className="hidden" data-testid="music-upload-input" /></label>
                        <div className="flex items-center gap-2"><div className="flex-1 h-px bg-white/10" /><span className="text-[8px] text-white/30">OR</span><div className="flex-1 h-px bg-white/10" /></div>
                        <div><Label className="text-white/60 text-[10px] flex items-center gap-1"><Link className="w-3 h-3" /> Paste Audio URL</Label><div className="flex gap-1 mt-0.5"><Input placeholder="https://...mp3" value={audioUrlInput} onChange={(e) => setAudioUrlInput(e.target.value)} className="bg-white/5 border-white/10 text-white text-[10px] h-7" data-testid="audio-url-input" /><Button size="sm" onClick={handleAudioUrl} disabled={!audioUrlInput.trim()} className="bg-cameroon-green hover:bg-cameroon-green/90 h-7 px-2 text-[10px]" data-testid="audio-url-btn">Load</Button></div></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {musicAudioUrl && <audio ref={musicAudioRef} src={musicAudioUrl} preload="auto" crossOrigin="anonymous" onLoadedMetadata={onAudioLoaded} className="hidden" />}
            {phase === 'setup' && <p className="text-center text-white/30 text-[10px]" data-testid="setup-hint">Max {MAX_DURATION}s &middot; CCA frame & intro added automatically</p>}
          </>
        )}

        {/* ══════════ UPLOAD MODE ══════════ */}
        {mode === 'upload' && (
          <>
            {!uploadFile ? (
              <div className="space-y-4">
                <label className="flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed border-white/20 hover:border-cameroon-yellow/50 cursor-pointer transition-colors bg-white/[0.02]" data-testid="upload-dropzone">
                  <Film className="w-10 h-10 text-cameroon-yellow/60" />
                  <div className="text-center">
                    <p className="text-white/70 text-sm font-medium">Tap to select a video</p>
                    <p className="text-white/30 text-[10px] mt-1">MP4, MOV, WebM &middot; Max 100MB</p>
                  </div>
                  <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" data-testid="upload-input" />
                </label>
                <p className="text-center text-white/30 text-[10px]">Your video will be fitted inside the CCA cultural frame with the festival intro prepended. The full video will be visible — nothing gets hidden by the frame borders.</p>
              </div>
            ) : (
              <>
                {/* Preview uploaded video */}
                <div className="relative bg-black rounded-xl overflow-hidden mx-auto" style={{ width: '100%', aspectRatio: `${REC_W}/${REC_H}` }} data-testid="upload-preview-viewport">
                  <video src={uploadPreviewUrl} controls playsInline className="w-full h-full object-contain bg-black" data-testid="upload-preview-video" />
                </div>

                <div className="space-y-3">
                  {!uplVideoUrl && !uplProcessing && !uplError && (
                    <Button onClick={startUploadProcessing} className="bg-cameroon-yellow hover:bg-cameroon-yellow/90 text-slate-900 font-semibold h-10 w-full" data-testid="apply-frame-btn">
                      <Film className="w-4 h-4 mr-2" /> Apply CCA Frame & Intro
                    </Button>
                  )}
                  <ResultActions processing={uplProcessing} videoUrl={uplVideoUrl} processingError={uplError} onRetry={retryUpload} onReset={resetUpload} resetLabel="Choose Another" />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideoRecorderPage;
